import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import {
	faArrowRight,
	faChevronDown,
	faCirclePlay,
	faPlus,
} from "@fortawesome/pro-regular-svg-icons";

import { CampaignDisplayStatus, CampaignSummary } from "@signalist/shared-types";

import { Icon } from "@/components";
import { Spinner } from "@/components/Spinner/Spinner";
import { classNames } from "@/utils/classNames";
import { ROUTE_CONFIG } from "@/routes/config";
import { AnchoredPopover } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/AnchoredPopover";

import { buildCampaignBuilderPath } from "../utils/campaignBuilderPath";

import { computeCampaignBulkState, useListCampaignSummaries } from "./ListCampaignSubRows";

type RollupTone = "green" | "amber" | "grey" | "muted";

/** Aggregate dot tone for the trigger, derived from the list's campaign summaries. */
const deriveTone = (summaries: CampaignSummary[] | undefined): RollupTone => {
	// Guard the empty/loading case first: `[].every()` is vacuously true → would misread as "ended".
	if (!summaries || summaries.length === 0) {
		return "muted";
	}

	const bulk = computeCampaignBulkState(summaries);

	if (bulk === "active") {
		return "green";
	}

	if (bulk === "partial") {
		return "amber";
	}

	if (bulk === "paused") {
		return "grey";
	}

	// No runnable campaigns: everything ended vs. nothing valid.
	const allEnded = summaries.every((campaign) => campaign.status === CampaignDisplayStatus.ENDED);

	return allEnded ? "grey" : "muted";
};

const DOT_TONE: Record<RollupTone, string> = {
	green: "bg-status-success",
	amber: "bg-status-warning",
	grey: "bg-gray-400",
	muted: "bg-gray-300",
};

/** Per-campaign status chip colours (mockup row-pill grammar). */
const ROW_CHIP: Record<CampaignDisplayStatus, string> = {
	[CampaignDisplayStatus.INVALID]: "bg-status-error-light text-status-error",
	[CampaignDisplayStatus.VALID]: "bg-blue-action-200 text-blue-action",
	[CampaignDisplayStatus.READY]: "bg-blue-action-200 text-blue-action",
	[CampaignDisplayStatus.ACTIVE]: "bg-status-success-light text-status-success",
	[CampaignDisplayStatus.PAUSED]: "bg-status-warning-light text-status-warning",
	[CampaignDisplayStatus.ENDED]: "bg-gray-100 text-gray-500",
	[CampaignDisplayStatus.LOADING]: "bg-blue-action-200 text-blue-action",
};

const pct = (num: number, den: number): number => (den > 0 ? Math.round((num / den) * 100) : 0);

/** One campaign row in the dropdown: status chip · name · contacted · positive. */
const CampaignRow: FunctionComponent<{
	campaign: CampaignSummary;
	onOpen: () => void;
	/** The popover portals to `document.body`, so the menu takes focus itself on open. */
	autoFocus?: boolean;
}> = ({ campaign, onOpen, autoFocus }) => {
	const { t } = useTranslation();

	return (
		<button
			className="grid w-full cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-3.5 rounded-lg px-3 py-2 text-left hover:bg-background-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-light"
			data-testid={`list-campaigns-dropdown-row-${campaign.campaignId}`}
			type="button"
			autoFocus={autoFocus}
			onClick={onOpen}
		>
			<span
				className={classNames(
					"inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11.5px] font-semibold",
					ROW_CHIP[campaign.status]
				)}
			>
				<span className="h-1.5 w-1.5 rounded-full bg-current" />
				{t(`campaign.builder.status.labels.${campaign.status}`)}
			</span>
			<span className="truncate text-[13px] font-semibold text-text" title={campaign.name}>
				{campaign.name}
			</span>
			<span className="whitespace-nowrap text-xs text-text-meta">
				<b className="font-bold text-text">{campaign.contacted}</b>/{campaign.total} (
				{pct(campaign.contacted, campaign.total)}%){" "}
				<span className="text-text-tertiary">{t("list.campaignRollup.contacted")}</span>
			</span>
			<span className="whitespace-nowrap text-xs text-text-meta">
				<b className="font-bold text-text">{pct(campaign.positive, campaign.contacted)}%</b>{" "}
				<span className="text-text-tertiary">{t("list.campaignRollup.positive")}</span>
			</span>
		</button>
	);
};

interface ListCampaignsDropdownProps {
	listId: number;
	/** From {@link listHasCampaigns} — drives the empty ("Create a campaign") vs. dropdown state. */
	hasCampaigns: boolean;
	/** Styling of the sibling header links, so the trigger matches them exactly. */
	triggerClassName: string;
	iconClassName: string;
}

/**
 * Stateful Campaigns control for the list header (PREC-3703). With no campaign yet it is a
 * "Create a campaign" link (auto-opens the branch picker via `?createCampaign=true`); once
 * campaigns exist it becomes a dropdown: aggregate status dot on the trigger, the per-campaign
 * breakdown rows (absorbed from the former ListCampaignRollup pill), and "Create a new campaign" /
 * "View campaigns" links. Fetches its own summaries from GET list/:id/campaign-summary — the lean
 * single-list payload doesn't carry them.
 */
export const ListCampaignsDropdown: FunctionComponent<ListCampaignsDropdownProps> = ({
	listId,
	hasCampaigns,
	triggerClassName,
	iconClassName,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	const listsForSummary = useMemo(() => [{ id: listId }], [listId]);
	const { getSummaries, refresh } = useListCampaignSummaries(listsForSummary);

	useEffect(() => {
		if (hasCampaigns) {
			refresh(listId);
		}
	}, [listId, hasCampaigns, refresh]);

	if (!hasCampaigns) {
		return (
			<Link
				className={triggerClassName}
				// `?createCampaign=true` makes the copywriting page auto-open the Event-vs-Regular
				// branch picker on arrival (see useAutoCreateCampaignParam).
				to={`${ROUTE_CONFIG.LISTS}/create/copywriting/${listId}?createCampaign=true`}
			>
				<Icon className={iconClassName} icon={faCirclePlay} />
				<span>{t("list.campaignRollup.create")}</span>
			</Link>
		);
	}

	const summaries = getSummaries(listId);
	const tone = deriveTone(summaries);

	const goToCampaigns = (createNew: boolean) => {
		setAnchorEl(null);
		navigate(
			`${ROUTE_CONFIG.LISTS}/create/copywriting/${listId}${createNew ? "?createCampaign=true" : ""}`
		);
	};

	// Open one campaign by its stable id (never by name), so equal names on one list cannot cross.
	const openCampaign = (campaign: CampaignSummary) => {
		setAnchorEl(null);
		navigate(
			buildCampaignBuilderPath(listId, {
				name: campaign.name,
				campaignId: campaign.campaignId,
			})
		);
	};

	// A dismissal removes the focused row from the DOM, dropping focus to `document.body`; put it
	// back on the trigger so a keyboard user can reopen the menu. Do not steal focus when it already
	// moved elsewhere (e.g. an outside click on another focusable control).
	const closeMenu = () => {
		const active = document.activeElement;
		const focusWasInMenu =
			!active ||
			active === document.body ||
			(active instanceof Node && !!menuRef.current?.contains(active));

		setAnchorEl(null);

		if (focusWasInMenu) {
			triggerRef.current?.focus();
		}
	};

	return (
		<>
			<button
				className={classNames(triggerClassName, "cursor-pointer")}
				data-testid="list-campaigns-dropdown-trigger"
				ref={triggerRef}
				type="button"
				onClick={(event) => {
					if (anchorEl) {
						closeMenu();

						return;
					}

					// Capture before the updater runs — `currentTarget` is nulled after dispatch.
					setAnchorEl(event.currentTarget as HTMLElement);
				}}
			>
				<span className={classNames("h-1.5 w-1.5 rounded-full", DOT_TONE[tone])} />
				<Icon className={iconClassName} icon={faCirclePlay} />
				<span>{t("list.campaignRollup.campaigns")}</span>
				<Icon className="h-2.5 w-2.5 opacity-60" icon={faChevronDown} />
			</button>

			{anchorEl && (
				<AnchoredPopover anchorEl={anchorEl} width={540} onClose={closeMenu}>
					<div
						ref={menuRef}
						className="overflow-hidden rounded-xl border border-border-light bg-white p-1.5 shadow-lg"
						data-testid="list-campaigns-dropdown-menu"
					>
						{summaries === undefined ? (
							<div className="flex items-center justify-center px-3 py-4">
								<Spinner color="#4d66f5" />
							</div>
						) : (
							summaries.map((campaign, index) => (
								<CampaignRow
									autoFocus={index === 0}
									campaign={campaign}
									key={campaign.campaignId}
									onOpen={() => openCampaign(campaign)}
								/>
							))
						)}
						<div className="mt-1 flex flex-col items-start gap-0.5 border-t border-border-light px-1.5 pb-1 pt-1.5">
							<button
								className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-1.5 py-1.5 text-[13px] font-semibold text-brand-light hover:bg-background-light"
								type="button"
								onClick={() => goToCampaigns(true)}
							>
								<Icon className="h-3 w-3" icon={faPlus} />
								{t("list.campaignRollup.createNew")}
							</button>
							<button
								className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-1.5 py-1.5 text-[13px] font-semibold text-brand-light hover:bg-background-light"
								type="button"
								onClick={() => goToCampaigns(false)}
							>
								{t("list.campaignRollup.view")}
								<Icon className="h-3 w-3" icon={faArrowRight} />
							</button>
						</div>
					</div>
				</AnchoredPopover>
			)}
		</>
	);
};
